<template>
  <div class="server-settings-container">
    <!-- 服务器设置卡片 -->
    <div class="settings-card">
      <div class="card-header">
        <h3 class="card-title">{{ $t('transferServer.localAsTransferServer') }}</h3>
        <p class="card-subtitle">
          {{ $t('transferServer.asClientHint') }}<br>
          {{ $t('transferServer.asServerHint') }}
          <span class="text-warning">({{ $t('transferServer.publicModeWarning') }})</span>
        </p>
      </div>

      <div class="card-body">
        <div class="table-responsive">
          <table class="server-config-table">
            <thead>
              <tr>
                <th>{{ $t('transferServer.name') }}</th>
                <th>{{ $t('transferServer.address') }}</th>
                <th>{{ $t('transferServer.port') }}</th>
                <th>{{ $t('transferServer.key') }}</th>
                <th>{{ $t('transferServer.secondAuth') }}</th>
                <th>{{ $t('transferServer.maxConnections') }}</th>
                <th>{{ $t('transferServer.mode') }}</th>
                <th>{{ $t('transferServer.remark') }}</th>
                <th>{{ $t('transferServer.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('transferServer.name')">{{ serverConfig.SubName }}</td>
                <td :data-label="$t('transferServer.address')">{{ serverConfig.IpAddr }}</td>
                <td :data-label="$t('transferServer.port')">{{ serverConfig.Port }}</td>
                <td :data-label="$t('transferServer.key')">
                  <div class="password-field">
                    <span v-if="showPrivToken">{{ serverConfig.PrivToken }}</span>
                    <span v-else>••••••</span>
                    <el-icon class="eye-icon" @click="showPrivToken = !showPrivToken">
                      <View v-if="showPrivToken" />
                      <Hide v-else />
                    </el-icon>
                  </div>
                </td>
                <td :data-label="$t('transferServer.secondAuth')">
                  <div class="password-field" v-if="serverConfig.SecondToken">
                    <span v-if="showSecondToken">{{ serverConfig.SecondToken }}</span>
                    <span v-else>••••••</span>
                    <el-icon class="eye-icon" @click="showSecondToken = !showSecondToken">
                      <View v-if="showSecondToken" />
                      <Hide v-else />
                    </el-icon>
                  </div>
                  <span v-else>{{ $t('transferServer.none') }}</span>
                </td>
                <td :data-label="$t('transferServer.maxConnections')">{{ serverConfig.MaxConnectNum }}</td>
                <td :data-label="$t('transferServer.mode')">
                  <span class="status-badge" :class="getStatusClass()">
                    {{ getServerStatusText() }}
                  </span>
                </td>
                <td :data-label="$t('transferServer.remark')">{{ serverConfig.Remark || $t('transferServer.none') }}</td>
                <td :data-label="$t('transferServer.operation')">
                  <el-button type="primary" size="small" @click="showEditDialog">
                    {{ $t('transferServer.editConfig') }}
                  </el-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 可用服务器列表 -->
    <div class="settings-card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h3 class="card-title">{{ $t('transferServer.availableServers') }}</h3>
            <p class="card-subtitle">
              {{ $t('transferServer.selectHint') }}
              <router-link to="/server-mapping" class="link">{{ $t('nav.serverMapping') }}</router-link>
              {{ $t('common.and') }}
              <router-link to="/p2p-client" class="link">{{ $t('nav.p2pClient') }}</router-link>
              <el-button type="warning" size="small" @click="forceCancelSelect">
                {{ $t('transferServer.forceCancelSelect') }}
              </el-button>
            </p>
          </div>
          <div class="header-actions">
            <el-button type="info" size="small" @click="showLogDialog">
              <el-icon><Document /></el-icon>
              {{ $t('transferServer.viewLogs') }}
            </el-button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div v-if="isLoading" class="loading-overlay">
          <el-icon class="is-loading"><Loading /></el-icon>
          {{ $t('transferServer.loading') }}
        </div>

        <div v-if="!isLoading && filteredServerList.length === 0" class="empty-state">
          <el-icon><Box /></el-icon>
          <p>{{ dataMsg || $t('transferServer.noData') }}</p>
        </div>

        <div v-if="filteredServerList.length > 0" class="table-responsive">
          <table class="server-list-table">
            <thead>
              <tr>
                <th>{{ $t('transferServer.name') }}</th>
                <th>{{ $t('transferServer.attribute') }}</th>
                <th>{{ $t('transferServer.address') }}</th>
                <th>{{ $t('transferServer.port') }}</th>
                <th>{{ $t('transferServer.p2pService') }}</th>
                <th>{{ $t('transferServer.secondAuth') }}</th>
                <th>{{ $t('transferServer.connectionsTotal') }}</th>
                <th>{{ $t('transferServer.remark') }}</th>
                <th>{{ $t('transferServer.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="server in filteredServerList" :key="server.Name"
                  :class="{ 'selected-row': server.SelectStatus }">
                <td :data-label="$t('transferServer.name')">
                  <span v-if="server.SelectStatus" class="selected-indicator">(*)</span>
                  {{ server.SubName }}
                </td>
                <td :data-label="$t('transferServer.attribute')">
                  <span class="server-type-badge" :class="getServerTypeClass(server)">
                    {{ getServerTypeText(server) }}
                  </span>
                </td>
                <td :data-label="$t('transferServer.address')">{{ server.IpAddr }}</td>
                <td :data-label="$t('transferServer.port')" :class="getPortStatusClass(server.Port)">
                  {{ server.Port === 0 ? $t('transferServer.notEnabled') : server.Port }}
                </td>
                <td :data-label="$t('transferServer.p2pService')" :class="getPortStatusClass(server.P2PServerPort)">
                  {{ server.P2PServerPort === 0 ? $t('transferServer.notEnabled') : server.P2PServerPort }}
                </td>
                <td :data-label="$t('transferServer.secondAuth')">
                  <span class="auth-badge" :class="server.SecondTokenFlag ? 'enabled' : 'disabled'">
                    {{ server.SecondTokenFlag ? $t('system.on') : $t('system.off') }}
                  </span>
                </td>
                <td :data-label="$t('transferServer.connectionsTotal')">
                  <div class="connection-progress">
                    <div class="progress-label">
                      {{ server.ConnectNum }}/{{ server.MaxConnectNum }}
                    </div>
                    <el-progress
                      :percentage="getConnectionPercentage(server)"
                      :color="getProgressColor(server)"
                      :show-text="false"
                      :stroke-width="16"
                    />
                  </div>
                </td>
                <td :data-label="$t('transferServer.remark')" class="remark-cell">{{ server.Remark }}</td>
                <td :data-label="$t('transferServer.operation')">
                  <el-button
                    v-if="server.SelectStatus"
                    type="danger"
                    size="small"
                    @click="unselectServer(server)"
                  >
                    {{ $t('transferServer.deselect') }}
                  </el-button>
                  <el-button
                    v-else
                    type="primary"
                    size="small"
                    @click="selectServer(server)"
                  >
                    {{ $t('transferServer.select') }}
                  </el-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 编辑服务器配置对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="$t('transferServer.editConfig')"
      :width="isMobile ? '95%' : '600px'"
      :before-close="handleCloseDialog"
      :destroy-on-close="true"
    >
      <el-form
        :model="editForm"
        label-width="120px"
        :rules="editRules"
        ref="editFormRef"
        :label-position="isMobile ? 'top' : 'right'"
      >
        <div class="form-grid">
          <el-form-item :label="$t('transferServer.name')" prop="SubName">
            <el-input v-model="editForm.SubName" disabled />
          </el-form-item>

          <el-form-item :label="$t('transferServer.address')" prop="IpAddr">
            <el-input v-model="editForm.IpAddr" :placeholder="$t('common.required')" />
          </el-form-item>

          <el-form-item :label="$t('transferServer.port')" prop="Port">
            <el-input
              v-model.number="editForm.Port"
              type="number"
              min="0"
              max="65535"
              placeholder="0-65535"
            />
          </el-form-item>

          <el-form-item :label="$t('transferServer.key')" prop="PrivToken">
            <el-input
              v-model="editForm.PrivToken"
              show-password
              placeholder="5-15位字母数字组合"
            />
          </el-form-item>

          <el-form-item :label="$t('transferServer.secondAuth')" prop="SecondToken">
            <el-input
              v-model="editForm.SecondToken"
              show-password
              :placeholder="$t('common.optional')"
            />
          </el-form-item>

          <el-form-item :label="$t('transferServer.maxConnections')" prop="MaxConnectNum">
            <el-input
              v-model.number="editForm.MaxConnectNum"
              type="number"
              min="1"
              max="999"
              placeholder="1-999"
            />
          </el-form-item>

          <el-form-item :label="$t('transferServer.mode')" prop="ServiceMode">
            <el-select
              v-model="editForm.ServiceMode"
              :placeholder="$t('common.required')"
              class="full-width"
            >
              <el-option :label="$t('transferServer.private')" value="private"></el-option>
              <el-option :label="$t('transferServer.public')" value="public"></el-option>
              <el-option :label="$t('common.no') + '(' + $t('system.off') + ')'" value="none"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('transferServer.remark')" prop="Remark">
            <el-input
              v-model="editForm.Remark"
              type="textarea"
              :rows="2"
              :placeholder="$t('common.optional')"
            />
          </el-form-item>
        </div>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="submitEditForm">{{ $t('system.saveSettings') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 服务器日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('log.title')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('log.logTotal', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('log.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">(5{{ $t('log.second') }})</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('log.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('common.refresh') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('log.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('log.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div 
            v-for="(log, index) in logsData" 
            :key="index" 
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { API_BASE_URL } from "@/config";
import { Loading, Box, View, Hide, Document, InfoFilled } from '@element-plus/icons-vue'

export default {
  name: 'ServerSettings',
  components: { Loading, Box, View, Hide, Document, InfoFilled },
  setup() {
    const router = useRouter()
    const serverConfig = ref({
      Name: '',
      SubName: '',
      IpAddr: '',
      Port: '',
      PrivToken: '',
      SecondToken: '',
      MaxConnectNum: '',
      Remark: '',
      ServiceMode: 'none',
      LocalSwitch: false,
      GlobalSwitch: false
    })

    const serverList = ref([])
    const dataMsg = ref('加载中...')
    const isLogin = ref(false)
    const loginUsername = ref('')
    const isLoading = ref(false)
    const intervalId = ref(null)
    const logRefreshInterval = ref(null)
    const showPrivToken = ref(false)
    const showSecondToken = ref(false)
    const isMobile = ref(false)

    // 日志对话框相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true) // 默认开启自动刷新

    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }

    // 计算属性：过滤有效的服务器列表
    const filteredServerList = computed(() => {
      return serverList.value.filter(server => server.Status)
    })

    // 获取服务器状态文本
    const getServerStatusText = () => {
      if (serverConfig.value.LocalSwitch) return '私人模式'
      if (serverConfig.value.GlobalSwitch) return '公共模式'
      return '无(已关闭)'
    }

    // 获取服务器状态样式类
    const getStatusClass = () => {
      if (serverConfig.value.LocalSwitch) return 'private'
      if (serverConfig.value.GlobalSwitch) return 'public'
      return 'disabled'
    }

    // 获取服务器类型文本
    const getServerTypeText = (server) => {
      if (server.IsBelong) return '官方'
      if (server.Global) return '公共'
      return '私人'
    }

    // 获取服务器类型样式类
    const getServerTypeClass = (server) => {
      if (server.IsBelong) return 'official'
      if (server.Global) return 'public'
      return 'private'
    }

    // 获取端口状态样式类
    const getPortStatusClass = (port) => {
      return port === 0 ? 'disabled' : 'enabled'
    }

    // 获取连接百分比
    const getConnectionPercentage = (server) => {
      return Math.min(100, (server.ConnectNum / server.MaxConnectNum) * 100)
    }

    // 获取进度条颜色
    const getProgressColor = (server) => {
      const percentage = server.ConnectNum / server.MaxConnectNum
      if (percentage < 0.7) return '#67C23A'
      if (percentage < 0.9) return '#E6A23C'
      return '#F56C6C'
    }

    // 辅助函数：将对象转换为URL编码的字符串
    const encodeFormData = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
        .join('&')
    }

    // 编辑对话框相关状态
    const editDialogVisible = ref(false)
    const editFormRef = ref(null)
    const editForm = ref({
      Name: '',
      SubName: '',
      IpAddr: '',
      Port: '',
      PrivToken: '',
      SecondToken: '',
      MaxConnectNum: '',
      Remark: '',
      ServiceMode: 'none'
    })

    // 表单验证规则
    const editRules = {
      IpAddr: [
        { required: true, message: '请输入IP地址', trigger: 'blur' },
        { message: '请输入有效的地址', trigger: 'blur' }
      ],
      Port: [
        { required: true, message: '请输入端口号', trigger: 'blur' },
        { type: 'number', min: 0, max: 65535, message: '端口号范围0-65535', trigger: 'blur' }
      ],
      PrivToken: [
        { required: true, message: '请输入密钥', trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9]{5,15}$/, message: '密钥为5-15个字符，只能是数字或字母', trigger: 'blur' }
      ],
      SecondToken: [
        { pattern: /^[a-zA-Z0-9]{0,20}$/, message: '二次密钥不能超过20个字符，只能是数字或字母', trigger: 'blur' }
      ],
      MaxConnectNum: [
        { required: true, message: '请输入最大连接数', trigger: 'blur' },
        { type: 'number', min: 1, max: 999, message: '最大连接数范围1-999', trigger: 'blur' }
      ]
    }

    // 显示编辑对话框
    const showEditDialog = () => {
      editForm.value = {
        Name: serverConfig.value.Name,
        SubName: serverConfig.value.SubName,
        IpAddr: serverConfig.value.IpAddr,
        Port: serverConfig.value.Port,
        PrivToken: serverConfig.value.PrivToken,
        SecondToken: serverConfig.value.SecondToken,
        MaxConnectNum: serverConfig.value.MaxConnectNum,
        ServiceMode: serverConfig.value.LocalSwitch ? 'private' : 
                   serverConfig.value.GlobalSwitch ? 'public' : 'none',
        Remark: serverConfig.value.Remark
      }
      editDialogVisible.value = true
    }

    // 关闭对话框前的确认
    const handleCloseDialog = (done) => {
      ElMessageBox.confirm('确定要放弃修改吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        done()
      }).catch(() => {})
    }

    // 提交编辑表单
    const submitEditForm = () => {
      editFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            const params = new URLSearchParams()
            params.append('name', editForm.value.Name)
            params.append('token', editForm.value.PrivToken)
            params.append('secondToken', editForm.value.SecondToken)
            params.append('ip', editForm.value.IpAddr)
            params.append('port', editForm.value.Port)
            params.append('maxConnectNum', editForm.value.MaxConnectNum)
            params.append('remark', editForm.value.Remark)
            
            let serviceResponse
            if (editForm.value.ServiceMode === 'private') {
              serviceResponse = await fetch(`${API_BASE_URL}/start-tfs`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                credentials: 'include',
                body: 'global=off'
              })
            } else if (editForm.value.ServiceMode === 'public') {
              serviceResponse = await fetch(`${API_BASE_URL}/start-tfs`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                credentials: 'include',
                body: 'global=on'
              })
            } else {
              serviceResponse = await fetch(`${API_BASE_URL}/stop-tfs`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                credentials: 'include',
                body: ''
              })
            }

            if (!serviceResponse.ok) {
              throw new Error('更新服务模式失败')
            }
            
            const configResponse = await fetch(`${API_BASE_URL}/update-local-server`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              credentials: 'include',
              body: params
            })

            if (!configResponse.ok) {
              throw new Error('更新服务器配置失败')
            }

            ElMessage.success('更新成功')
            editDialogVisible.value = false
            fetchData()
          } catch (error) {
            ElMessage.error(error.message || '更新失败')
          }
        } else {
          ElMessage.warning('请填写正确的表单信息')
          return false
        }
      })
    }

    // 选择服务器
    const selectServer = async (server) => {
      if (server.SecondToken) {
        try {
          const { value: password } = await ElMessageBox.prompt('请输入密码', '二次认证', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputPattern: /^[a-zA-Z0-9]{1,20}$/,
            inputErrorMessage: '密码格式不正确'
          })

          const encryptedPassword = CryptoJS.MD5(password).toString();

          if (encryptedPassword !== server.SecondToken) {
            ElMessage.error('密码错误')
            return
          }
        } catch (error) {
          return
        }
      }

      try {
        const params = encodeFormData({
          name: server.Name
        })

        const response = await fetch(`${API_BASE_URL}/select-server`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error('选择服务器失败')
        }

        const data = await response.json()
        
        if (data.errMsg.includes('unstopped services')) {
          ElMessage.warning('请先停止所有端口映射服务!')
        } else if (data.errMsg.includes('reach the maximum')) {
          ElMessage.warning('服务器连接数达到上限!')
        } else {
          ElMessage.success(data.errMsg)
          fetchData()
        }
      } catch (error) {
        ElMessage.error(error.message || '选择失败')
      }
    }

    // 取消选择服务器
    const unselectServer = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/unselect-server`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: ''
        })

        if (!response.ok) {
          throw new Error('取消选择服务器失败')
        }

        const data = await response.json()
        
        if (data.errMsg.includes('unstopped services')) {
          ElMessage.warning('请先停止所有端口映射服务!')
        } else {
          ElMessage.success(data.errMsg)
          fetchData()
        }
      } catch (error) {
        ElMessage.error(error.message || '取消选择失败')
      }
    }

    // 强制取消选择
    const forceCancelSelect = async () => {
      try {
        await ElMessageBox.confirm('是否强制取消选择的服务器?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await fetch(`${API_BASE_URL}/forceCancelSelect-server`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: ''
        })

        if (!response.ok) {
          throw new Error('强制取消选择失败')
        }

        const data = await response.json()
        
        if (data.errMsg.includes('unstopped services')) {
          ElMessage.warning('请先停止所有端口映射服务!')
        } else {
          ElMessage.success(data.errMsg)
          fetchData()
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.message || '操作失败')
        }
      }
    }

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success('已开启自动刷新（5秒/次）')
      } else {
        stopLogAutoRefresh()
        ElMessage.info('已关闭自动刷新')
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }
        
        const params = new URLSearchParams({
          module: 'transferserver',
          page: logCurrentPage.value,
          size: logPageSize.value
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统')
          router.push('/user-login?callback=transfer-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        logsData.value = data.data || []
        logTotal.value = data.count || 0
        
        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error('获取日志失败:', error)
        if (showLoading) {
          ElMessage.error(`获取日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm('确定要清空所有中转服务器日志吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        logClearing.value = true
        
        const params = new URLSearchParams({
          module: 'transferserver'
        })
        
        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统')
          router.push('/user-login?callback=transfer-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '清空日志失败')
        }
        
        ElMessage.success('日志已清空')
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('清空日志失败:', error)
          ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true
        
        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'transferserver',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统')
          router.push('/user-login?callback=transfer-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        const allLogs = data.data || []
        
        if (allLogs.length === 0) {
          ElMessage.warning('暂无日志可下载')
          return
        }
        
        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `transfer_server_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`)
      } catch (error) {
        console.error('下载日志失败:', error)
        ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }

    // 获取数据
    const fetchData = async () => {
      if (isLoading.value) return
      
      try {
        isLoading.value = true
        const response = await fetch(`${API_BASE_URL}/transfer-server`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error('获取服务器列表失败')
        }
        
        const result = await response.json()

        if (result.errCode === 1000) {
          ElMessage.error('请先登录系统')
          router.push('/user-login?callback=transfer-server')
          return
        }

        if (result.errCode !== 0) {
          throw new Error(result.errMsg || '获取设置信息失败');
        }

        serverConfig.value = {
          Name: result.Name,
          SubName: result.SubName,
          IpAddr: result.IpAddr,
          Port: result.Port,
          PrivToken: result.PrivToken,
          SecondToken: result.SecondToken,
          MaxConnectNum: result.MaxConnectNum,
          Remark: result.Remark,
          LocalSwitch: result.LocalSwitch,
          GlobalSwitch: result.GlobalSwitch
        }

        serverList.value = result.data || []
        isLogin.value = true
        loginUsername.value = result.loginUsername || ''
        dataMsg.value = result.dataMsg || '暂无数据'

      } catch (error) {
        console.error('获取数据失败:', error)
        ElMessage.error(error.message || '获取数据失败')
        serverList.value = []
        dataMsg.value = '获取数据失败，请刷新重试'
      } finally {
        isLoading.value = false
      }
    }

    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }

    // 初始化数据
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchData()
      
      // 每5秒刷新一次数据
      intervalId.value = setInterval(() => {
        fetchData()
      }, 5000)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }
      stopLogAutoRefresh()
    })

    return {
      serverConfig,
      serverList,
      filteredServerList,
      dataMsg,
      isLogin,
      loginUsername,
      isLoading,
      showPrivToken,
      showSecondToken,
      isMobile,
      editDialogVisible,
      editForm,
      editFormRef,
      editRules,
      showEditDialog,
      handleCloseDialog,
      submitEditForm,
      getServerStatusText,
      getStatusClass,
      getServerTypeText,
      getServerTypeClass,
      getPortStatusClass,
      getConnectionPercentage,
      getProgressColor,
      fetchData,
      selectServer,
      unselectServer,
      forceCancelSelect,
      // 日志相关
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped lang="scss">
.server-settings-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  .card-header {
    padding: 20px;
    border-bottom: 1px solid #ebeef5;
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 8px;
    }
    
    .card-subtitle {
      font-size: 14px;
      color: #606266;
      margin: 0;
      
      .text-warning {
        color: #e6a23c;
      }
    }
    
    .d-flex {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
    }
    
    .header-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .card-body {
    padding: 20px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      
      .card-title {
        font-size: 16px;
      }
      
      .card-subtitle {
        font-size: 12px;
      }
      
      .d-flex {
        flex-direction: column;
        align-items: flex-start;
      }
      
      .header-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
    
    .card-body {
      padding: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
    }
    
    .card-body {
      padding: 8px;
    }
  }
}

.link {
  color: #409eff;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.server-config-table,
.server-list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
}

.selected-row {
  background-color: #f0f7ff !important;
  
  td {
    font-weight: 500;
  }
}

.status-badge,
.server-type-badge,
.auth-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.status-badge {
  &.private {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.public {
    background-color: #ecf5ff;
    color: #409eff;
  }
  
  &.disabled {
    background-color: #f5f5f5;
    color: #909399;
  }
}

.server-type-badge {
  &.official {
    background-color: #fdf6ec;
    color: #e6a23c;
  }
  
  &.public {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.private {
    background-color: #ecf5ff;
    color: #409eff;
  }
}

.auth-badge {
  &.enabled {
    background-color: #fdf6ec;
    color: #e6a23c;
  }
  
  &.disabled {
    background-color: #f5f5f5;
    color: #909399;
  }
}

.enabled {
  color: #67c23a;
  font-weight: 500;
}

.disabled {
  color: #909399;
}

.selected-indicator {
  color: #f56c6c;
  margin-right: 4px;
}

.password-field {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .eye-icon {
    cursor: pointer;
    color: #909399;
    font-size: 16px;
    transition: color 0.2s;
    
    &:hover {
      color: #409eff;
    }
  }
}

.connection-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .progress-label {
    min-width: 60px;
    font-size: 12px;
    color: #606266;
  }
  
  :deep(.el-progress) {
    flex-grow: 1;
  }
}

.remark-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
    animation: rotating 2s linear infinite;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.full-width {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式处理 */
@media (max-width: 992px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .server-config-table,
  .server-list-table {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &[data-label="操作"] {
        .el-button {
          flex-shrink: 0;
        }
      }
    }
  }
  
  .password-field {
    flex-wrap: wrap;
    justify-content: flex-end;
    
    span {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .connection-progress {
    flex-wrap: wrap;
    width: 100%;
    
    .progress-label {
      min-width: auto;
    }
    
    :deep(.el-progress) {
      width: 100%;
      margin-top: 4px;
    }
  }
  
  .el-dialog {
    margin: 8px !important;
  }
}

@media (max-width: 480px) {
  .server-config-table,
  .server-list-table {
    td {
      padding: 6px 8px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
      
      .el-button--small {
        padding: 4px 8px;
        font-size: 12px;
      }
    }
  }
  
  .status-badge,
  .server-type-badge,
  .auth-badge {
    padding: 2px 6px;
    font-size: 11px;
  }
  
  .password-field {
    .eye-icon {
      font-size: 14px;
    }
    
    span {
      max-width: 120px;
    }
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>